import type { ExportingMimeTypeValue, ExportingOptions, Options } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

declare module 'highcharts' {
  interface Chart {
    exportChart: (options: ExportingOptions, chartOptions?: Options) => void;
  }
}

type ExportChartImageParams = {
  chart: Highcharts.Chart;
  type: ExportingMimeTypeValue;
  filename: string;
  options: Highcharts.Options;
  exportingOptions?: Omit<Highcharts.ExportingOptions, 'type' | 'filename'>;
};

// Lazily pre-load jsPDF + svg2pdf so Highcharts offline-exporting skips the
// CDN fetch and Cairo is always registered as "HighchartsFont".
// Highcharts skips pdfFont loading when SVG text is ASCII-only, leaving
// text elements with font-family "HighchartsFont" that jsPDF cannot resolve
// unless we pre-register it here.
const ensurePdfDeps = async (): Promise<void> => {
  if ((globalThis as Record<string, unknown>).jspdf) return;

  const [{ jsPDF }] = await Promise.all([import('jspdf'), import('svg2pdf.js')]);

  try {
    const res = await fetch('/fonts/Cairo.ttf');
    if (res.ok) {
      const blob = await res.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result.split(',')[1]);
          } else {
            reject(new Error('font read failed'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Push a single "initialized" handler — runs on every new jsPDF instance
      (jsPDF as any).API.events.push([
        'initialized',
        function (this: any) {
          for (const style of ['normal', 'bold', 'italic', 'bolditalic']) {
            this.addFileToVFS(style, base64);
            this.addFont(style, 'HighchartsFont', style);
          }
          this.setFont('HighchartsFont');
        }
      ]);
    }
  } catch {
    // best-effort — PDF falls back to default fonts if Cairo is unavailable
  }

  (globalThis as Record<string, unknown>).jspdf = { jsPDF };
};

export const exportChartImage = async ({
  chart,
  type,
  filename,
  options,
  exportingOptions
}: ExportChartImageParams): Promise<void> => {
  if (type === 'application/pdf') {
    await ensurePdfDeps();
  }
  chart.exportChart({ type, filename, ...exportingOptions }, options);
};
